<template>
	<section class="resources-assignment-page has-menu-builder" :class="{ 'menu-builder-empty': !hasSelectedApplication }">
		<v-layout class="rol-section assignment-toolbar px-5">
			<v-flex xs12 class="role-picker">
				<v-select
				solo
				hide-details
				return-object
				label="Roles"
				item-text="name"
				:items="roles"
				@input="getData($event, 'rolSelected', 'codeRole', 'apps')"
				></v-select>
			</v-flex>
			<v-flex xs12 class="role-create-action">
				<v-btn
					v-if="canFinishConfiguration"
					type="button"
					class="btn-action caption assignment-finish-button"
					color="primary"
					:disabled="finalizing"
					:loading="finalizing"
					@click.stop="finishConfiguration"
				>
					Finalizar configuración
					<v-icon class="ml-2" size="18">done_all</v-icon>
				</v-btn>
				<v-btn
					type="button"
					color="primary"
					class="create-role-button"
					@click="newRol"
					>
					Crear rol
					<v-icon color="white">add</v-icon>
				</v-btn>
			</v-flex>
		</v-layout>
		<section v-if="!appsExist" class="assignment-empty-state">
			<span class="assignment-empty-icon"><v-icon>device_hub</v-icon></span>
			<small>CONFIGURACIÓN GUIADA</small>
			<h2>Construye el menú de acceso</h2>
			<p>Selecciona un rol para comenzar a organizar sus aplicaciones, módulos y recursos.</p>
			<div class="assignment-empty-steps">
				<span><i>1</i> Elige un rol</span>
				<v-icon>chevron_right</v-icon>
				<span><i>2</i> Selecciona la aplicación</span>
				<v-icon>chevron_right</v-icon>
				<span><i>3</i> Ordena el menú</span>
			</div>
		</section>
		<nav v-if="appsExist" class="assignment-progress px-5" aria-label="Flujo de asignación">
			<span class="progress-step" :class="{ 'is-complete': hasSelectedApplication, 'is-active': !hasSelectedApplication }"><i>1</i> Aplicación</span>
			<v-icon>chevron_right</v-icon>
			<span class="progress-step" :class="{ 'is-complete': hasSelectedModule, 'is-active': hasSelectedApplication && !hasSelectedModule }"><i>2</i> Módulo</span>
			<v-icon>chevron_right</v-icon>
			<span class="progress-step" :class="{ 'is-active': hasSelectedModule }"><i>3</i> Recursos</span>
		</nav>
		<div v-if="hasSelectedApplication" class="selection-path mx-5">
			<div class="selection-path-item">
				<span class="path-icon app-selection"><v-icon color="white">apps</v-icon></span>
				<div class="selection-path-copy"><small>Aplicación · {{ enabledAppsCount }} de {{ apps.length }} habilitadas</small><strong>{{ getAppName }}</strong></div>
				<div class="path-actions">
					<span v-if="configured(app)" class="path-status">Habilitada</span>
					<button type="button" class="menu-preview-mobile-trigger" title="Ver menú" @click="openMenuPreview()"><v-icon>format_list_numbered</v-icon></button>
					<button type="button" class="change-selection-button" title="Cambiar aplicación" @click="changeApplication"><v-icon>swap_horiz</v-icon></button>
				</div>
			</div>
			<v-icon v-if="hasSelectedModule" class="path-divider">chevron_right</v-icon>
			<div v-if="hasSelectedModule" class="selection-path-item">
				<span class="path-icon module-selection"><v-icon color="white">view_module</v-icon></span>
				<div class="selection-path-copy"><small>Módulo · {{ enabledModulesCount }} de {{ modules.length }} habilitados</small><strong>{{ getModuleName }}</strong></div>
				<div class="path-actions">
					<span v-if="configured(module)" class="path-status">Habilitado</span>
					<app-tooltip-button color="success" icon="settings" text="Configurar módulo" @click-icon="openModuleAndResourceConfiguration('module', module)" />
				</div>
			</div>
		</div>
		<transition name="fade-down" mode="out-in">
			<div v-if="appsExist && !hasSelectedApplication" class="assignment-panel mx-5">
				<v-layout>
					<v-flex xs12>
						<div class="assignment-section-header">
							<div><v-icon color="primary">apps</v-icon><h3>Aplicaciones</h3></div>
							<div class="assignment-summary">
								<span class="enabled-count">{{ enabledAppsCount }} habilitadas</span>
								<span>{{ apps.length }} en total</span>
							</div>
						</div>
						<div class="responsive-grid">
							<app-button
								v-for="app in sortedApps"
								:key="app.id || app.codeApp"
								:active="Boolean(app.isActive)"
								:configured="configured(app)"
								:icon-path="assignmentIcon(app)"
								:icons-to-use="appUseIcon(app)"
								:item="app"
								:subtitle="assignmentSubtitle(app)"
								state-text="Habilitada"
								@erase-configuration="deleteApplicationConfiguration"
								@button-clicked="getData($event, 'app', 'codeApp', 'modules')"
							/>
						</div>
					</v-flex>
				</v-layout>
			</div>
		</transition>
		<transition name="fade-down" mode="out-in">
			<v-layout class="module-section assignment-panel mx-5" v-if="modulesExist && !hasSelectedModule">
				<v-flex xs12>
					<div class="assignment-section-header">
						<div><v-icon color="primary">view_module</v-icon><h3>{{getAppName}} · Módulos</h3></div>
						<div class="assignment-summary">
							<span class="enabled-count">{{ enabledModulesCount }} habilitados</span>
							<span>{{ modules.length }} en total</span>
						</div>
					</div>
					<div class="responsive-grid">
						<app-button
							v-for="mod in sortedModules"
							:key="mod.id || mod.idModule"
							:active="Boolean(mod.isActive)"
							:configured="configured(mod)"
							:icon-path="assignmentIcon(mod)"
							:icons-to-use="moduleUseIcon(mod)"
							:item="mod"
							:subtitle="moduleSubtitle(mod)"
							state-text="Habilitado"
							@erase-configuration="deleteConfiguration('moduleIds', $event)"
							@button-clicked="getData($event, 'module', 'idModule', 'resources')"
							@edit-button="openModuleAndResourceConfiguration('module', $event)"
						/>
					</div>
				</v-flex>
			</v-layout>
		</transition>
		<transition name="fade-down" mode="out-in">
			<div v-if="resourcesExist" class="assignment-panel resource-assignment-panel mx-5">
				<v-layout>
					<v-flex xs12>
						<div class="assignment-section-header">
							<div><v-icon color="primary">device_hub</v-icon><h3>{{getAppName}} · {{getModuleName}}</h3></div>
						</div>
						<div class="resource-table-card">
							<div class="resource-table-toolbar">
								<div class="resource-search" :class="{ 'is-filled': resourceSearch }">
									<v-icon>search</v-icon>
									<input
										type="text"
										placeholder="Buscar por nombre, URL o recurso padre…"
										v-model="resourceSearch"
									>
									<button
										v-if="resourceSearch"
										type="button"
										title="Limpiar búsqueda"
										@click="resourceSearch = ''"
									>
										<v-icon>close</v-icon>
									</button>
								</div>
								<div class="resource-filter-chips">
									<button
										v-for="filter in resourceFilters"
										:key="filter.code"
										type="button"
										class="resource-filter-chip"
										:class="[
											`resource-filter-chip--${filter.code}`,
											{ 'is-active': resourceFilter === filter.code },
										]"
										:title="filter.hint"
										@click="resourceFilter = filter.code"
									>
										{{ filter.label }}<strong>{{ filter.count }}</strong>
									</button>
								</div>
							</div>
							<div class="resource-table-guide">
								<div class="resource-table-guide-text">
									<v-icon color="primary" size="20">info_outline</v-icon>
									<span>Marca el checkbox para activar un recurso, ordena con las flechas y configura su nombre.</span>
								</div>
								<div class="resource-table-summary">
									<span v-if="pendingChangesCount" class="summary-chip summary-chip-pending">
										<v-icon>cloud_upload</v-icon>
										{{ pendingChangesCount }} sin guardar
									</span>
									<span class="summary-chip summary-chip-active">
										{{activeResourcesCount}} activos
									</span>
									<span class="summary-chip">
										{{inactiveResourcesCount}} inactivos
									</span>
								</div>
							</div>
							<div v-if="noResourcesForFilter" class="resource-filter-empty">
								<v-icon>filter_list</v-icon>
								<div>
									<strong>Ningún recurso coincide con el filtro</strong>
									<small>{{ resources.length }} recursos disponibles en este módulo.</small>
								</div>
								<button type="button" @click="clearResourceFilters">Limpiar filtros</button>
							</div>
							<app-table
								selectable
								class="resources-table is-not-selectable"
								name="resources"
								:input-search="false"
								:columns="columns"
								:check-all="checkAllResources"
								:rows="resourcesComputed"
								@on-selected-all="selectAllResources"
								@row-is-selected="resourceSelectAction"
							>
								<template slot="rows" slot-scope="props">
									<td>
										{{ props.row.name }}
									</td>
									<td>
										{{ props.row.uri }}
									</td>
									<td>
										{{ getResourceName(props.row) }}
									</td>
									<td>
										<div class="resource-languages-cell">
											<span
												v-for="languageStatus in resourceLanguagesStatus(props.row)"
												:key="`${props.row.id}-${languageStatus.code}`"
												class="language-chip"
												:class="{ 'language-chip-filled': languageStatus.filled }"
												:title="languageStatus.filled ?
													languageStatus.name : 'Sin nombre configurado'"
											>
												<v-icon>
													{{ languageStatus.filled ? 'check_circle' : 'error_outline' }}
												</v-icon>
												{{ languageStatus.code }}
											</span>
										</div>
									</td>
									<td class="resource-position-cell">
										<div class="position-control">
											<v-text-field
												solo
												hide-details
												type="number"
												min="1"
												:disabled="!props.row.isSelected"
												:value="props.row.position"
												@change="changeResourcePosition(props.row, $event)"
											/>
											<div class="position-steppers">
												<button
													type="button"
													title="Subir"
													:disabled="!canMoveResource(props.row, -1)"
													@click="moveResource(props.row, -1)"
												>
													<v-icon>keyboard_arrow_up</v-icon>
												</button>
												<button
													type="button"
													title="Bajar"
													:disabled="!canMoveResource(props.row, 1)"
													@click="moveResource(props.row, 1)"
												>
													<v-icon>keyboard_arrow_down</v-icon>
												</button>
											</div>
										</div>
									</td>
									<td>
										<div class="resource-icon-preview">
											<span class="resource-icon-empty">—</span>
											<img
												v-if="resourceIcon(props.row)"
												:src="resourceIcon(props.row)"
												:alt="`Ícono de ${props.row.name}`"
												@error="hideBrokenImage"
											>
										</div>
									</td>
									<td>
										<app-tooltip-button
											color="green"
											icon="settings"
											text="Conf. de Recursos"
											@click-icon="openModuleAndResourceConfiguration(
												'resource',
												props.row,
											)"
										/>
										<app-tooltip-button
											:disabled="props.row.disabled"
											color="primary"
											icon="timeline"
											text="Barra de acciones"
											@click-icon="openActionsBarModal(props.row)"
										/>
										<app-tooltip-button
											color="primary"
											icon="cloud_queue"
											text="Servicios web"
											@click-icon="loadServices(props.row)"
										/>
									</td>
								</template>
							</app-table>
						</div>
					</v-flex>
				</v-layout>
			</div>
		</transition>
		<transition name="menu-preview-fade">
			<button
				v-if="showMenuPreview"
				type="button"
				class="menu-preview-backdrop"
				aria-label="Cerrar vista del menú"
				@click="showMenuPreview = false"
			></button>
		</transition>
		<transition name="menu-preview-slide">
			<aside class="menu-preview-panel" :class="{ 'is-open': showMenuPreview }" aria-label="Vista del menú configurado">
				<header class="menu-preview-header">
					<div>
						<span class="menu-preview-icon"><v-icon color="white">format_list_numbered</v-icon></span>
						<div><small>ORDEN CONFIGURADO</small><h3>Vista del menú</h3></div>
					</div>
					<button type="button" aria-label="Cerrar" @click="showMenuPreview = false"><v-icon>close</v-icon></button>
				</header>
				<div v-if="hasSelectedApplication" class="menu-preview-context">
					<v-icon>apps</v-icon>
					<div><small>Aplicación</small><strong>{{ getAppName }}</strong></div>
					<span>{{ configuredModulesPreview.length }} módulos</span>
				</div>
				<div v-if="hasSelectedApplication && configuredModulesPreview.length" class="menu-preview-list">
					<section
						v-for="mod in configuredModulesPreview"
						:key="mod.id || mod.idModule"
						class="menu-preview-group"
						:class="{ 'is-current': isCurrentModule(mod) }"
					>
						<div class="menu-preview-module-row">
							<button type="button" class="menu-preview-item" @click="selectModuleFromPreview(mod)">
								<span class="menu-preview-position">{{ modulePositionLabel(mod) }}</span>
								<span class="menu-entry-icon module-menu-icon">
									<v-icon>view_module</v-icon>
									<img v-if="moduleIcon(mod)" :src="moduleIcon(mod)" :alt="`Ícono de ${moduleDisplayName(mod)}`" @error="hideBrokenImage">
								</span>
								<span class="menu-preview-name"><strong>{{ moduleDisplayName(mod) }}</strong><small>{{ isCurrentModule(mod) ? `${moduleResourcesForPreview(mod).length} submenús activos` : 'Módulo configurado' }}</small></span>
								<v-icon>{{ isCurrentModule(mod) ? 'expand_more' : 'chevron_right' }}</v-icon>
							</button>
							<button
								type="button"
								class="menu-preview-delete-module"
								title="Eliminar módulo"
								@click="deleteConfiguration('moduleIds', mod)"
							>
								<v-icon>delete_outline</v-icon>
							</button>
						</div>
						<div v-if="isCurrentModule(mod) && moduleResourcesForPreview(mod).length" class="menu-preview-submenu">
							<button
								v-for="resourceItem in moduleResourcesForPreview(mod)"
								:key="resourceItem.id"
								type="button"
								@click="selectModuleFromPreview(mod)"
							>
								<span>{{ resourcePositionLabel(resourceItem) }}</span>
								<span class="menu-entry-icon resource-menu-icon">
									<v-icon>insert_drive_file</v-icon>
									<img v-if="resourceIcon(resourceItem)" :src="resourceIcon(resourceItem)" :alt="`Ícono de ${resourceDisplayName(resourceItem)}`" @error="hideBrokenImage">
								</span>
								<strong>{{ resourceDisplayName(resourceItem) }}</strong>
							</button>
						</div>
						<div v-else-if="isCurrentModule(mod)" class="menu-preview-no-submenu">Sin submenús activos</div>
					</section>
				</div>
				<div v-else-if="hasSelectedApplication" class="menu-preview-empty">
					<v-icon>playlist_add</v-icon>
					<strong>Aún no hay módulos configurados</strong>
					<span>Selecciona un módulo para comenzar a construir el menú.</span>
				</div>
				<div v-else class="menu-preview-empty">
					<v-icon>touch_app</v-icon>
					<strong>Elige una aplicación</strong>
					<span>Aquí se irá armando el menú: los módulos y sus submenús, en el orden que definas.</span>
				</div>
				<button
					v-if="hasSelectedApplication"
					type="button"
					class="add-module-button"
					@click="showModulePicker"
				>
					<span><v-icon>add</v-icon></span>
					<div><strong>Agregar módulo</strong><small>Ver módulos disponibles</small></div>
					<v-icon>chevron_right</v-icon>
				</button>
				<footer v-if="hasSelectedApplication"><v-icon>info_outline</v-icon> El orden se actualiza al guardar cada módulo.</footer>
			</aside>
		</transition>
		<action-sidebar
			:title="action.title"
			:value="action.open"
			:width="action.code === 'bar-actions' ? 780 : 600"
			:is-selected="action.IsSelected"
			@close-modal="closeAssignmentSidebar"
		>
			<bar-actions
				v-if="action.code === 'bar-actions'"
				:actions="actions"
				@close-modal="clearTemporalSelected"
				@save-data="saveBarActionData"
				@select-all="selectAll"
				@row-is-selected="rowIsSelected"
			/>
			<new-rol-component
				v-if="action.code === 'rol'"
				@close-modal="closeRolModal"
			/>
			<resource-configuration
				v-if="chooseAction"
				:resource="chooseResource"
				:require-icon="action.code === 'module'"
				:suggested-position="suggestedPosition"
				@set-image="setImageResource"
				@close-modal="closeConfiguration"
				@save-data="saveData"
			/>
			<resource-web-service
				v-if="action.code === 'list-service'"
				:web-services="webServices"
				:check-all="checkAllServices"
				@close="clearTemporalSelected"
				@select-all="selectAllServices"
				@save-web-service="saveWebServices"
				@row-is-selected="rowIsSelectedServices"
			/>
		</action-sidebar>
		<section class="modal-filter-footer">
			<modal-sidebar-button
				:is-loading="modelEmpty"
				@accept-button-clicked="saveAssignmentChanges"
				@cancel-button-clicked="cancelAction"
			/>
		</section>
		<v-dialog v-model="distributionQuestion" persistent max-width="520">
			<v-card class="distribution-question-card">
				<div class="distribution-question-accent"></div>
				<v-card-title class="distribution-question-title">
					<span class="distribution-question-icon"><v-icon>cloud_done</v-icon></span>
					<div class="distribution-question-heading">
						<small>PROCESO COMPLETADO</small>
						<strong>Configuración guardada</strong>
					</div>
					<button type="button" class="distribution-question-close" aria-label="Cerrar" @click="stayOnAssignment">
						<v-icon>close</v-icon>
					</button>
				</v-card-title>
				<v-card-text class="distribution-question-content">
					<h3>¿Deseas distribuir los recursos ahora?</h3>
					<p>La configuración quedó guardada. Puedes continuar editándola o avanzar a la distribución del template.</p>
					<div class="distribution-question-note">
						<v-icon>info_outline</v-icon>
						<span>La distribución aplicará esta configuración a las compañías que selecciones.</span>
					</div>
				</v-card-text>
				<v-card-actions class="distribution-question-actions">
					<v-btn type="button" class="distribution-secondary-action" flat @click="stayOnAssignment">
						Seguir configurando
					</v-btn>
					<v-btn type="button" class="distribution-primary-action" color="primary" @click="goToDistribution">
						Distribuir ahora
						<v-icon class="ml-2" size="18">arrow_forward</v-icon>
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</section>
</template>
<script>
import appButton from '@/components/shared/app-button';
import appTooltipButton from '@/components/shared/app-tooltip-button';
import actionSidebar from '@/components/shared/action-sidebar';
import resourceConfiguration from '@/components/resources/resource-configuration';
import newRolComponent from '@/components/roles/new-rol-component';
import barActions from '@/components/actions/actions-bar';
import resourceWebService from '@/components/resource-service/resource-web-service';
import lib from '@/lib';

const {
	compose,
	createPropertyByOtherOne,
	equality,
	find,
	getDeeper,
	setNewProperty,
	setNewPropertyByFunction,
	updateItemInCollection,
} = lib;

function created() {
	({ codeProject: this.codeProject, idTemplate: this.idTemplate } = this.$route.params);
	this.templateCode = this.$route.query.codeTemplate || null;
	this.loadResources('roles');
	this.loadLanguages();
}

function assignmentEnabled(item) {
	if (typeof item.hasConfiguredChildren === 'boolean') return item.hasConfiguredChildren;
	if (typeof item.isSelected === 'boolean') return item.isSelected;
	return Boolean(item.flagStatus || item.flagActions);
}

async function loadResources(flag, action = null) {
	const headers = {
		Accept: `${process.env.ACCEPT_SERVICE_V2}`,
	};
	const url = `/project/${this.codeProject}/template/${this.idTemplate}/${flag}`;
	const { data: response } = await this.$http.get(url, { headers, params: this.params });
	({ data: this[flag] } = response);
	if (flag === 'modules') {
		this.syncCurrentAppConfiguration(this.modules.some(assignmentEnabled));
	} else if (flag === 'resources') {
		this.updateResources();
		if (this.resources.some(resource => resource.isSelected)) {
			this.syncCurrentAppConfiguration(true);
		}
	} else if (flag === 'actions') {
		if (this.temporalSelected.length > 0 && this.model.roleIds) {
			this.actions = this.actions.map((a) => {
				const newRow = { ...a };
				const newFlag = this.model.roleIds[0].resources.filter(r => r.id === action.id);
				const flagIndex = newFlag.length && newFlag[0].actions ?
					newFlag[0].actions.findIndex(f => f === a.id) : -1;
				newRow.isSelected = flagIndex > -1 || a.isSelected;
				return newRow;
			});
		}
		this.dataSelected = this.actions.filter(a => a.isSelected);
	}
}

function evaluateTemplateLanguages(item) {
	const exist = getDeeper('templateLanguages')(item);
	return !exist;
}

function updateResources() {
	this.resources = this.resources.map(
		setNewProperty('disabled', false),
	);
	this.normalizeResourcePositions(null, false);
	this.resources.forEach((resource) => {
		const key = this.resourceSelectionKey(resource);
		this.$set(this.resourceSelectionBaseline, key, Boolean(resource.isSelected));
	});
}

function resourceSelectionKey(resource) {
	const selectionRole = this.rolSelected.code || this.params.codeRole || 'role';
	const moduleId = this.module.id || this.params.idModule || 'module';
	return `${selectionRole}:${moduleId}:${resource.id}`;
}

function clonePendingModel(model) {
	return JSON.parse(JSON.stringify(model || {}));
}

function resourcePosition(resource) {
	return Number(getDeeper('templateLanguages', 'position')(resource) || resource.position) ||
		Number.MAX_SAFE_INTEGER;
}

function normalizeResourcePositions(preferredResource = null, queueChanges = true) {
	const preferredId = preferredResource ? preferredResource.id : null;
	const requestedPosition = preferredResource ? Number(preferredResource.position) : null;
	const byPosition = (first, second) =>
		this.resourcePosition(first) - this.resourcePosition(second);
	const active = this.resources.filter(resource => resource.isSelected).sort(byPosition);
	const inactive = this.resources.filter(resource => !resource.isSelected).sort(byPosition);

	if (preferredId && requestedPosition) {
		const currentIndex = active.findIndex(resource => resource.id === preferredId);
		if (currentIndex > -1) {
			const selected = active.splice(currentIndex, 1)[0];
			const destination = Math.max(0, Math.min(requestedPosition - 1, active.length));
			active.splice(destination, 0, selected);
		}
	}

	this.resources = active.concat(inactive).map((resource, index) => {
		const updated = { ...resource, position: index + 1 };
		if (updated.templateLanguages) {
			updated.templateLanguages = { ...updated.templateLanguages, position: index + 1 };
			if (queueChanges && updated.isSelected) {
				this.queueResourcePosition(updated);
			}
		}
		return updated;
	});
}

function queueResourcePosition(resource) {
	const languages = this.resourceLanguages(resource);
	const configuredResource = {
		...resource,
		flagStatus: true,
		flagActions: true,
		flagDelete: false,
		languages,
	};
	const currentResources = this.model.resourceIds || [];
	const index = currentResources.findIndex(item => item.id === resource.id);
	if (index > -1) {
		this.model.resourceIds.splice(index, 1, configuredResource);
	} else {
		this.$set(this.model, 'resourceIds', currentResources.concat(configuredResource));
	}
}

function resourceLanguages(resource) {
	const configuredLanguages = getDeeper('templateLanguages', 'languages')(resource) ||
		resource.languages;
	if (configuredLanguages && Object.keys(configuredLanguages).length) {
		return configuredLanguages;
	}
	return this.languages.reduce((result, language) => {
		const key = String(language.id || '').toLowerCase();
		if (!key) return result;
		return {
			...result,
			[key]: {
				...language,
				name: resource.name || resource.title || '',
				isSelected: true,
			},
		};
	}, {});
}

function prepareCurrentResourcesForSave() {
	if (!this.hasSelectedModule) return;
	this.resources
		.filter(resource => resource.isSelected)
		.forEach((resource) => {
			const languages = this.resourceLanguages(resource);
			const configuredResource = {
				...resource,
				position: this.resourcePosition(resource),
				flagStatus: true,
				flagActions: true,
				flagDelete: false,
				languages,
			};
			this.queueConfigurationItem('resourceIds', configuredResource);
			this.updateRoleAndResources(configuredResource);
		});
}

function changeResourcePosition(resource, position) {
	this.normalizeResourcePositions({ ...resource, position: Number(position) });
}

async function loadLanguages() {
	const headers = {
		Accept: `${process.env.ACCEPT_SERVICE_V2}`,
	};
	try {
		const { data: project } = await this.$http.get(`projects/${this.codeProject}`, { headers });
		const projectLanguages = project.data.languages;
		const self = this;
		Object.keys(projectLanguages).forEach((pLangKey) => {
			const currentLanguage = projectLanguages[pLangKey];
			self.languages = self.languages.concat(currentLanguage);
		});
	} catch (err) {
		this.showError();
	}
}

function getData(obj, vueVariable, flagParam, flag) {
	this.setDefaultValues(flagParam);
	this[vueVariable] = { ...obj };
	this.updateIsActiveProperty(flagParam, obj);
	const moduleConfigured = assignmentEnabled(obj);
	if (flag === 'resources' && !moduleConfigured) {
		this.params[flagParam] = obj.id;
		this.module.position = this.nextModulePosition;
		this.openModuleAndResourceConfiguration('module', this.module, true);
		return;
	}
	this.params[flagParam] = flag === 'resources' ? obj.id : obj.code || obj[flagParam] || obj.id;
	this.loadResources(flag, obj);
}

function updateIsActiveProperty(flagParam, obj) {
	const opt = {
		codeApp: 'apps',
		idModule: 'modules',
	};
	const varSelected = opt[flagParam];
	if (varSelected) {
		this[varSelected] = this[varSelected].reduce((list, item) => {
			const newItem = { ...item };
			newItem.isActive = false;
			if (obj.id === item.id) {
				newItem.isActive = true;
			}
			return list.concat(newItem);
		}, []);
	}
}

function setDefaultValues(flag) {
	const opt = {
		codeRole: this.clearData,
		codeApp: this.clearIdModuleAnResources,
	};
	if (opt[flag]) {
		opt[flag].call();
	}
}

function clearIdModuleAnResources() {
	this.actions = [];
	this.barActionSelected = {};
	this.module = {};
	this.modules = [];
	this.params.idModule = null;
	this.resource = null;
	this.resources = [];
}

function hasSelectedApplication() {
	return Boolean(this.app.id || this.app.codeApp || this.params.codeApp);
}

function hasSelectedModule() {
	return Boolean(this.module.id || this.module.idModule || this.params.idModule);
}

function changeApplication() {
	this.showMenuPreview = false;
	this.actions = [];
	this.app = {};
	this.module = {};
	this.modules = [];
	this.resource = {};
	this.resources = [];
	this.params.codeApp = null;
	this.params.idModule = null;
	this.params.idResource = null;
	this.apps = this.apps.map(app => ({ ...app, isActive: false }));
}

function changeModule() {
	this.showMenuPreview = false;
	this.actions = [];
	this.module = {};
	this.resource = {};
	this.resources = [];
	this.params.idModule = null;
	this.params.idResource = null;
	this.modules = this.modules.map(module => ({ ...module, isActive: false }));
}

function clearData() {
	this.showMenuPreview = false;
	this.hasSavedConfigurationChanges = false;
	this.actions = [];
	this.app = {};
	this.apps = [];
	this.barActionSelected = {};
	this.module = {};
	this.modules = [];
	this.resource = null;
	this.resources = [];
	this.params = {
		codeApp: null,
		codeRole: null,
		idModule: null,
		idResource: null,
		page: null,
		perPage: null,
	};
}

function getAppName() {
	return getDeeper('name')(this.app);
}

function getModuleName() {
	return getDeeper('name')(this.module);
}

function getResourceName({ resourceId }) {
	const { name } = (find(equality('id', resourceId), this.resources) || {});
	return name || '--';
}

function assignmentIcon(item) {
	return item.pathIcon || getDeeper('templateLanguages', 'pathIcon')(item) || '';
}

function assignmentSubtitle(app) {
	return String(app.code || app.codeApp || '').trim();
}

function moduleSubtitle(mod) {
	const position = this.modulePositionLabel(mod);
	return assignmentEnabled(mod) && position !== '—' ? `Posición ${position}` : '';
}

function moduleIcon(mod) {
	return mod.pathIcon || getDeeper('templateLanguages', 'pathIcon')(mod) || '';
}

function resourceIcon(resource) {
	return resource.pathIcon || getDeeper('templateLanguages', 'pathIcon')(resource) || '';
}

function hideBrokenImage({ target }) {
	target.classList.add('is-hidden');
}

function resourcesExist() {
	return this.resources.length > 0;
}

function modulesExist() {
	return this.modules.length > 0;
}

function appsExist() {
	return this.apps.length > 0;
}

function activeResourcesCount() {
	return this.resources.filter(resource => resource.isSelected).length;
}

function inactiveResourcesCount() {
	return this.resources.length - this.activeResourcesCount;
}

function nextPosition(items) {
	const positions = items
		.filter(assignmentEnabled)
		.map(item => Number(getDeeper('templateLanguages', 'position')(item) || item.position))
		.filter(position => Number.isFinite(position) && position > 0);
	return positions.length ? Math.max(...positions) + 1 : 1;
}

function nextModulePosition() {
	return nextPosition(this.modules);
}

function suggestedPosition() {
	return this.action.code === 'module' ? this.nextModulePosition : nextPosition(this.resources);
}

function openModuleAndResourceConfiguration(flag, reso, required = false) {
	this.updateCurrentModuleOrResource(flag, reso);
	if (flag !== 'list-service') {
		const opt = {
			module: this.actionModule,
			resource: this.actionResource,
		};
		this.action = { ...opt[flag] };
		this.configurationRequired = required;
	}
}

function closeConfiguration() {
	const pendingResource = this.pendingResourceConfiguration;
	this.defaultAction();
	if (pendingResource) {
		this.cancelResourceActivation(pendingResource);
	}
	if (this.configurationRequired) {
		this.configurationRequired = false;
		this.changeModule();
	}
}

function cancelResourceActivation(pendingResource) {
	const storedResource = find(equality('id', pendingResource.id), this.resources) ||
		pendingResource;
	if (!storedResource.isSelected) return;
	this.resourceSelectAction({ ...storedResource, isSelected: false }, true, false);
}

function closeAssignmentSidebar() {
	if (this.chooseAction) {
		this.closeConfiguration();
	} else {
		this.clearTemporalSelected();
	}
}

function defaultAction() {
	this.pendingResourceConfiguration = null;
	this.action = {
		code: null,
		open: false,
		title: '',
		templateLanguagesArray: [],
	};
}

function clearTemporalSelected() {
	this.action = {
		code: null,
		open: false,
		title: '',
	};
	if (this.dataSelected.length > 0) {
		this.dataSelected = [];
	}
}

function updateCurrentModuleOrResource(flag, reso) {
	const { templateLanguages } = reso;
	let newFlag = flag;
	if (flag === 'list-service') {
		newFlag = 'resource';
	}
	if (templateLanguages) {
		const languages = this.getLanguages(templateLanguages);
		this[newFlag] = setNewProperty('templateLanguagesArray', languages)(reso);
	} else {
		const languages = this.languages.map(setNewProperty('name', ''));
		this[newFlag] = setNewProperty('templateLanguagesArray', languages)(reso);
	}
}

function getLanguages({ languages }) {
	const currentLanguages = languages || {};
	return Object.keys(currentLanguages).map(lan => currentLanguages[lan]);
}

function itemLanguages(item) {
	return getDeeper('templateLanguages', 'languages')(item) || item.languages || null;
}

function resourceLanguagesStatus(resource) {
	const languages = this.itemLanguages(resource) || {};
	const namesByCode = Object.keys(languages).reduce((result, key) => ({
		...result,
		[String(key).toLowerCase()]: String((languages[key] || {}).name || '').trim(),
	}), {});
	const codes = this.languages.length ?
		this.languages.map(language => String(language.id || '')) : ['En', 'Es'];
	return codes.filter(code => code).map((code) => {
		const name = namesByCode[code.toLowerCase()] || '';
		return { code: code.toUpperCase(), filled: Boolean(name), name };
	});
}

function isConfigured(item) {
	const languages = this.itemLanguages(item);
	if (!languages) return false;
	const names = Object.keys(languages).map(key => (languages[key] || {}).name);
	return Boolean(names.length) && names.every(name => String(name || '').trim());
}

function setImageResource(img) {
	const flag = this.action.code;
	if (flag === 'module' || flag === 'resource') {
		this[flag].pathIcon = img.urlImage;
	}
}

function newRol() {
	this.action = { ...this.actionNewRol };
}

function openActionsBarModal(action) {
	this.actions = [];
	this.resource = { ...action };
	this.getData(action, 'resource', 'idResource', 'actions');
	this.action = { ...this.actionBarActions };
}

function appUseIcon(app) {
	return assignmentEnabled(app) || app.isActive ? ['close'] : [];
}

function syncCurrentAppConfiguration(hasConfiguration) {
	if (!this.app || (!this.app.id && !this.app.codeApp)) return;
	this.app = { ...this.app, hasConfiguredChildren: hasConfiguration };
	this.apps = this.apps.map((app) => {
		const isCurrent = this.app.id ? app.id === this.app.id :
			app.codeApp === this.app.codeApp;
		return isCurrent ? { ...app, hasConfiguredChildren: hasConfiguration } : app;
	});
}

function moduleUseIcon() {
	return ['edit'];
}

function sortAssignmentItems(first, second) {
	const enabledDifference = Number(assignmentEnabled(second)) - Number(assignmentEnabled(first));
	if (enabledDifference !== 0) {
		return enabledDifference;
	}
	const activeDifference = Number(Boolean(second.isActive)) - Number(Boolean(first.isActive));
	if (activeDifference !== 0) {
		return activeDifference;
	}
	return String(first.name || '').localeCompare(String(second.name || ''), 'es');
}

function sortedApps() {
	return [...this.apps].sort(sortAssignmentItems);
}

function sortedModules() {
	return [...this.modules].sort(sortAssignmentItems);
}

function modulePosition(mod) {
	const position = Number(getDeeper('templateLanguages', 'position')(mod) || mod.position);
	return Number.isFinite(position) && position > 0 ? position : Number.MAX_SAFE_INTEGER;
}

function configuredModulesPreview() {
	return this.modules
		.filter(assignmentEnabled)
		.sort((first, second) => {
			const positionDifference = modulePosition(first) - modulePosition(second);
			if (positionDifference !== 0) {
				return positionDifference;
			}
			return this.moduleDisplayName(first).localeCompare(this.moduleDisplayName(second), 'es');
		});
}

function moduleDisplayName(mod) {
	const languages = getDeeper('templateLanguages', 'languages')(mod) || mod.languages || {};
	const spanish = languages.es || languages.Es || {};
	const english = languages.en || languages.En || {};
	return spanish.name || english.name || mod.name || 'Módulo sin nombre';
}

function modulePositionLabel(mod) {
	const position = modulePosition(mod);
	return position === Number.MAX_SAFE_INTEGER ? '—' : position;
}

function isCurrentModule(mod) {
	const currentId = this.module.id || this.module.idModule;
	const moduleId = mod.id || mod.idModule;
	return Boolean(currentId && moduleId && String(currentId) === String(moduleId));
}

function selectModuleFromPreview(mod) {
	if (!this.isCurrentModule(mod)) {
		this.getData(mod, 'module', 'idModule', 'resources');
	}
	this.showMenuPreview = false;
}

function showModulePicker() {
	this.changeModule();
	this.showMenuPreview = false;
}

function resourceDisplayName(resourceItem) {
	const languages = getDeeper('templateLanguages', 'languages')(resourceItem) ||
		resourceItem.languages || {};
	const spanish = languages.es || languages.Es || {};
	const english = languages.en || languages.En || {};
	return spanish.name || english.name || resourceItem.name || 'Submenú sin nombre';
}

function resourcePositionLabel(resourceItem) {
	const position = this.resourcePosition(resourceItem);
	return position === Number.MAX_SAFE_INTEGER ? '—' : position;
}

function moduleResourcesForPreview(mod) {
	if (!this.isCurrentModule(mod)) {
		return [];
	}
	return this.resources
		.filter(resourceItem => resourceItem.isSelected && !resourceItem.flagDelete)
		.sort((first, second) => {
			const positionDifference = this.resourcePosition(first) - this.resourcePosition(second);
			if (positionDifference !== 0) {
				return positionDifference;
			}
			return this.resourceDisplayName(first)
				.localeCompare(this.resourceDisplayName(second), 'es');
		});
}

function openMenuPreview() {
	this.showMenuPreview = true;
}

function enabledAppsCount() {
	return this.apps.filter(assignmentEnabled).length;
}

function enabledModulesCount() {
	return this.modules.filter(assignmentEnabled).length;
}

function chooseAction() {
	return this.action.code === 'module' || this.action.code === 'resource';
}

function chooseResource() {
	let returnedObj = {};
	if (this.action.code === 'module') {
		returnedObj = { ...this.module };
	} else if (this.action.code === 'resource') {
		returnedObj = { ...this.resource };
	}
	return returnedObj;
}

function updateRoleIdsInfo(resource) {
	const newResource = compose(
		setNewProperty('flagActions', true),
		setNewProperty('flagStatus', true),
		setNewProperty('isSelected', true),
	)(resource);
	this.updateRoleAndResources(newResource);
	this.resources = updateItemInCollection('id', newResource, this.resources);
}

function configured(mod) {
	return assignmentEnabled(mod);
}

function cancelAction() {
	this.$router.go(-1);
}

async function saveInfo(body, flagDelete, vueArray) {
	const newBody = { ...body };
	const hasPayload = Object.keys(newBody).some((key) => {
		const value = newBody[key];
		return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined;
	});
	if (!hasPayload) return false;
	if (newBody.resourceIds) {
		newBody.resourceIds = body.resourceIds.map((resource) => {
			const newResource = { ...resource };
			const keys = Object.keys(resource.languages);
			newResource.languages = keys.reduce((acum, k) => {
				const newAcum = { ...acum };
				const newKey = k.toLowerCase();
				newAcum[newKey] = resource.languages[k];
				return newAcum;
			}, {});
			return newResource;
		});
	}
	if (newBody.roleIds) {
		newBody.roleIds = newBody.roleIds.map((roleId) => {
			const newRoleId = { ...roleId };
			newRoleId.typeRoleId = this.rolSelected.typeRoleId;
			return newRoleId;
		});
	}
	const url = `distribution-template/${this.idTemplate}`;
	const headers = {
		Accept: `${process.env.ACCEPT_SERVICE_V2}`,
	};
	try {
		await this.$http.patch(url, newBody, { headers });
		this.showNotification('Actualización exitosa');
		if (vueArray) {
			await this.loadResources(vueArray);
		}
	} catch (error) {
		this.showError('Los cambios no se guardaron', 'info');
		return false;
	}
	if (!flagDelete) {
		this.resources.forEach((resource) => {
			const key = this.resourceSelectionKey(resource);
			this.$set(this.resourceSelectionBaseline, key, Boolean(resource.isSelected));
		});
		this.resourceSelectionUndo = {};
		this.model = {};
	}
	return true;
}

async function finishConfiguration() {
	this.finalizing = true;
	try {
		const saved = this.modelEmpty ? true : await this.saveInfo(this.model);
		if (!saved) return;
		if (!this.templateCode) {
			const headers = { Accept: `${process.env.ACCEPT_SERVICE_V2}` };
			const { data: response } = await this.$http.get(`template/${this.idTemplate}`, { headers });
			const template = response.data || response;
			this.templateCode = template.codeTemplate;
		}
		if (!this.templateCode) {
			this.showError('No se pudo identificar el código del template', 'info');
			return;
		}
		this.distributionQuestion = true;
	} catch (error) {
		this.showError('No se pudo preparar la distribución', 'info');
	} finally {
		this.finalizing = false;
	}
}

async function saveAssignmentChanges() {
	this.distributionQuestion = false;
	this.prepareCurrentResourcesForSave();
	if (this.modelEmpty) return false;
	const hadChanges = !this.modelEmpty;
	const saved = await this.saveInfo(this.model);
	if (saved && hadChanges) {
		this.hasSavedConfigurationChanges = true;
		if (this.hasSelectedModule) {
			await this.loadResources('resources', this.module);
		} else if (this.hasSelectedApplication) {
			await this.loadResources('modules', this.app);
		}
	}
	return saved;
}

function stayOnAssignment() {
	this.distributionQuestion = false;
}

function goToDistribution() {
	this.distributionQuestion = false;
	this.goTo('page-distribution-of-resources', {
		params: { idDistribution: this.templateCode },
	});
}

function closeRolModal({ reCharge }) {
	this.defaultAction();
	if (reCharge) {
		this.loadResources('roles');
	}
}

function selectAll(value) {
	this.actions = this.actions.map(setNewProperty('isSelected', value));
}

function rowIsSelected(row) {
	this.actions = updateItemInCollection('id', row, this.actions);
	if (row.isSelected) {
		this.dataSelected.push(row);
	} else {
		const index = this.dataSelected.findIndex(r => r.id === row.id);
		if (index > -1) this.dataSelected.splice(index, 1);
	}
}

function getActionsSelected(actions) {
	return actions.reduce((list, item) => {
		if (item.isSelected) {
			return list.concat(item.id);
		}
		return list;
	}, []);
}

async function deleteApplicationConfiguration(item) {
	const headers = { Accept: `${process.env.ACCEPT_SERVICE_V2}` };
	const params = {
		...this.params,
		codeApp: item.codeApp || item.code,
		idModule: null,
	};
	try {
		const url = `/project/${this.codeProject}/template/${this.idTemplate}/modules`;
		const { data: response } = await this.$http.get(url, { headers, params });
		const hasActiveModules = (response.data || []).some(assignmentEnabled);
		if (hasActiveModules) {
			this.showError('Primero elimina los módulos activos de esta aplicación', 'info');
			return;
		}
		await this.deleteConfiguration('appIds', item);
	} catch (error) {
		this.showError('No se pudo comprobar la configuración de la aplicación', 'info');
	}
}

async function deleteConfiguration(flag, item) {
	if (flag === 'moduleIds') {
		await this.deleteModuleOnly(item);
		return;
	}
	const opt = {
		appIds: [compose(
			setNewProperty('flagStatus', true),
			setNewProperty('flagActions', false),
			setNewProperty('isSelected', false),
			setNewProperty('flagDelete', true),
		), 'apps'],
	};
	const body = {};
	body[flag] = [opt[flag][0].call(null, item)];
	const vueArray = opt[flag][1];
	await this.saveInfo(body, 'delete', vueArray);
}

async function deleteModuleOnly(item) {
	const moduleToDelete = this.deleteModuleConfiguration(item);
	await this.saveInfo({ moduleIds: [moduleToDelete] }, 'delete', 'modules');
}

function deleteModuleConfiguration(item) {
	const { templateLanguages } = item;
	const { languages } = templateLanguages;
	return compose(
		setNewProperty('flagStatus', false),
		setNewProperty('isSelected', false),
		setNewProperty('flagActions', false),
		setNewProperty('flagDelete', true),
		setNewProperty('languages', languages),
		setNewProperty('codeRole', this.roleCode),
	)(item);
}

function createTemplateLanguages(flag) {
	this[flag].templateLanguages = { languages: {} };
	this[flag].templateLanguagesArray.forEach((lan) => {
		this[flag].templateLanguages.languages[lan.id] = lan;
	});
}

function queueConfigurationItem(collection, item) {
	const items = this.model[collection] || [];
	const index = items.findIndex(current => current.id === item.id);
	if (index > -1) {
		this.model[collection].splice(index, 1, item);
	} else {
		this.$set(this.model, collection, items.concat(item));
	}
}

function saveData(resource, newPosition) {
	const resour = resource;
	const flag = this.action.code;
	this[flag].pathIcon = resour.pathIcon;
	this.createTemplateLanguages(flag);
	const languages = { ...this[flag].templateLanguages.languages };
	const newLanguages = {};
	newLanguages.es = languages.Es || languages.es;
	newLanguages.en = languages.En || languages.en;
	this[flag] = compose(
		setNewProperty('disabled', false),
		setNewProperty('isSelected', true),
		setNewProperty('flagStatus', true),
		setNewProperty('flagActions', true),
		setNewProperty('flagDelete', false),
		setNewProperty('languages', newLanguages),
		setNewProperty('codeRole', this.roleCode),
	)(this[flag]);
	const keys = {
		resource: 'resourceIds',
		module: 'moduleIds',
	};
	this.queueConfigurationItem(keys[flag], this[flag]);
	if (flag === 'resource') {
		this.updateRoleIdsInfo(this[flag]);
		const index = this.resources.findIndex(r => r.id === resour.id);
		const position = Number(newPosition ||
			getDeeper('templateLanguages', 'position')(resour) || resour.position) || 1;
		this[flag].position = position;
		if (index > -1) {
			const currentResource = this.resources[index];
			if (!currentResource.templateLanguages) {
				this.$set(currentResource, 'templateLanguages', { languages: {} });
			}
			this.$set(currentResource.templateLanguages, 'position', position);
			this.$set(currentResource, 'position', position);
		}
	} else {
		const indexMo = this.modules.findIndex(r => r.id === resour.id);
		this.modules[indexMo].position = Number(newPosition ||
		resour.templateLanguages.position);
		this[flag].position = Number(newPosition ||
		resour.templateLanguages.position);
		const array = `${flag}s`;
		this[array] = updateItemInCollection('id', this[flag], this[array]);
	}
	this.defaultAction();
	if (flag === 'module') {
		this.configurationRequired = false;
		this.loadResources('resources', this.module);
	}
}

function modelEmpty() {
	return Object.keys(this.model).length === 0;
}

function hasSelectedRole() {
	return Boolean(this.rolSelected && (this.rolSelected.code || this.rolSelected.id));
}

function canFinishConfiguration() {
	return this.hasSelectedRole &&
		(!this.modelEmpty || this.hasSavedConfigurationChanges);
}

function inverseValue(prop) {
	return function inner(item) {
		return !item[prop];
	};
}

function resourceSelectAction(resource, normalizePositions = true, askConfiguration = true) {
	const selectionKey = this.resourceSelectionKey(resource);
	const currentSelection = Boolean(resource.isSelected);
	const hasBaseline = Object.prototype.hasOwnProperty.call(
		this.resourceSelectionBaseline,
		selectionKey,
	);
	const baselineSelection = hasBaseline ?
		this.resourceSelectionBaseline[selectionKey] : !currentSelection;
	const undoState = this.resourceSelectionUndo[selectionKey];
	if (currentSelection === baselineSelection && undoState) {
		const currentModel = JSON.stringify(this.model || {});
		if (currentModel === undoState.after) {
			this.model = clonePendingModel(undoState.before);
			this.resources = updateItemInCollection('id', resource, this.resources);
			this.normalizeResourcePositions(null, false);
			this.syncCheckAllResources();
			this.$delete(this.resourceSelectionUndo, selectionKey);
			return;
		}
	}
	if (!undoState && currentSelection !== baselineSelection) {
		this.$set(this.resourceSelectionUndo, selectionKey, {
			before: clonePendingModel(this.model),
			after: '',
		});
	}
	const newResource = compose(
		setNewPropertyByFunction('flagDelete', this.inverseValue('isSelected')),
		setNewProperty('flagStatus', true),
		setNewProperty('flagActions', true),
	)(resource);
	this.updateRoleAndResources(newResource);
	this.resources = updateItemInCollection('id', newResource, this.resources);
	if (normalizePositions) {
		this.normalizeResourcePositions();
	}
	this.syncCheckAllResources();
	if (this.resourceSelectionUndo[selectionKey]) {
		this.resourceSelectionUndo[selectionKey].after = JSON.stringify(this.model || {});
	}
	if (askConfiguration && newResource.isSelected && !this.isConfigured(newResource)) {
		const storedResource = find(equality('id', newResource.id), this.resources) || newResource;
		this.openModuleAndResourceConfiguration('resource', storedResource);
		this.pendingResourceConfiguration = { ...storedResource };
	}
}

function selectAllResources(value) {
	const resources = [...this.resourcesComputed];
	resources.forEach((resource) => {
		if (Boolean(resource.isSelected) !== value) {
			this.resourceSelectAction({ ...resource, isSelected: value }, false, false);
		}
	});
	this.normalizeResourcePositions();
	this.syncCheckAllResources();
}

function updateRoleAndResources(newResource) {
	if (getDeeper('roleIds')(this.model)) {
		const { code } = this.rolSelected;
		const index = this.model.roleIds.findIndex(role => role.code === code);
		if (index > -1) {
			const resume = this.model.roleIds[index].resources;
			const { id: resourceId } = newResource;
			const i = this.model.roleIds[index].resources.findIndex(re => re.id === resourceId);
			if (i > -1) {
				this.model.roleIds[index].resources.splice(i, 1, newResource);
			} else {
				this.model.roleIds[index].resources = resume.concat(newResource);
			}
		} else {
			const roleSelected = setNewProperty('resources', [newResource])(this.rolSelected);
			this.model.roleIds = this.model.roleIds.concat([roleSelected]);
		}
	} else {
		const roleSelected = this.setNewRole([newResource]);
		this.model.roleIds = [].concat([roleSelected]);
	}
}

function matchesResourceSearch(resource, term) {
	if (!term) return true;
	return [resource.name, resource.uri, this.getResourceName(resource)]
		.some(value => String(value || '').toLowerCase().indexOf(term) > -1);
}

function matchesResourceFilter(resource) {
	if (this.resourceFilter === 'active') return Boolean(resource.isSelected);
	if (this.resourceFilter === 'inactive') return !resource.isSelected;
	if (this.resourceFilter === 'pending') return !this.isConfigured(resource);
	return true;
}

function resourcesComputed() {
	const term = String(this.resourceSearch || '').trim().toLowerCase();
	return this.resources.filter(resource =>
		this.matchesResourceFilter(resource) && this.matchesResourceSearch(resource, term));
}

function pendingResourcesCount() {
	return this.resources.filter(resource => !this.isConfigured(resource)).length;
}

function pendingChangesCount() {
	return (this.model.resourceIds || []).length;
}

function resourceFilters() {
	return [
		{
			code: 'all',
			count: this.resources.length,
			hint: 'Todos los recursos del módulo',
			label: 'Todos',
		},
		{
			code: 'active',
			count: this.activeResourcesCount,
			hint: 'Recursos ya activados para este rol',
			label: 'Activos',
		},
		{
			code: 'inactive',
			count: this.inactiveResourcesCount,
			hint: 'Recursos que aún no se activan',
			label: 'Inactivos',
		},
		{
			code: 'pending',
			count: this.pendingResourcesCount,
			hint: 'Recursos sin nombre configurado en algún idioma',
			label: 'Sin nombre',
		},
	];
}

function noResourcesForFilter() {
	return Boolean(this.resources.length) && !this.resourcesComputed.length;
}

function clearResourceFilters() {
	this.resourceSearch = '';
	this.resourceFilter = 'all';
}

function canMoveResource(resource, delta) {
	if (!resource.isSelected) return false;
	const position = Number(resource.position);
	if (!Number.isFinite(position)) return false;
	return delta < 0 ? position > 1 : position < this.activeResourcesCount;
}

function moveResource(resource, delta) {
	if (!this.canMoveResource(resource, delta)) return;
	this.normalizeResourcePositions({
		...resource,
		position: Number(resource.position) + delta,
	});
}

function syncCheckAllResources() {
	const visible = this.resourcesComputed;
	this.checkAllResources = Boolean(visible.length) && visible.every(row => row.isSelected);
}

function saveBarActionData() {
	const actions = this.getActionsSelected(this.actions);
	const languages = this.resourceLanguages(this.resource);
	const updatedResource = {
		...this.resource,
		actions,
		isSelected: true,
		flagStatus: true,
		flagActions: true,
		flagDelete: false,
		languages,
	};
	this.queueConfigurationItem('resourceIds', updatedResource);
	this.updateRoleAndResources(updatedResource);
	this.resources = updateItemInCollection('id', updatedResource, this.resources);
	this.temporalSelected = this.actions.filter(action => action.isSelected);
	this.defaultAction();
	this.dataSelected = [];
}

function checkForResourceIds(id, templateLanguages) {
	const { languages } = templateLanguages;
	const newResource = compose(
		setNewProperty('languages', languages),
		setNewProperty('flagStatus', true),
		setNewProperty('flagActions', true),
		createPropertyByOtherOne('id_module', 'moduleId'),
	)(this.resource);
	if (this.model.resourceIds) {
		const indexResource = this.model.resourceIds.findIndex(equality('id', id));
		if (indexResource > -1) {
			this.model.resourceIds[indexResource] = { ...newResource };
		} else {
			this.model.resourceIds = this.model.resourceIds.concat({ ...newResource });
		}
	} else {
		this.model.resourceIds = [newResource];
	}
}

function checkForRoleIds(id, code, resources, actions) {
	if (this.model.roleIds) {
		const indexRol = this.model.roleIds.findIndex(equality('code', code));
		if (indexRol > -1) {
			const index = this.model.roleIds[indexRol].resources.findIndex(equality('id', id));
			if (index > -1) {
				this.model.roleIds[indexRol].resources[index].actions = [...actions];
			} else {
				this.model.roleIds[indexRol].resources
					= this.model.roleIds[indexRol].resources.concat(resources);
			}
		} else {
			const newRole = this.setNewRole(resources);
			this.model.roleIds = this.model.roleIds.concat(newRole);
		}
	} else {
		const newRole = this.setNewRole(resources);
		this.model.roleIds = [].concat(newRole);
	}
}

function roleCode() {
	const { code } = this.rolSelected;
	return code === null || code === undefined ? code : String(code);
}

function setNewRole(resources) {
	return compose(
		setNewProperty('flagStatus', true),
		setNewProperty('resources', resources),
		createPropertyByOtherOne('nameRole', 'name'),
		setNewProperty('codeRole', this.roleCode),
	)(this.rolSelected);
}

async function loadServices(row) {
	this.openModuleAndResourceConfiguration('list-service', row);
	try {
		const headers = {
			Accept: `${process.env.ACCEPT_SERVICE_V2}`,
		};
		const params = {
			codeApp: this.app.codeApp,
			codeRole: this.rolSelected.code,
			idModule: row.moduleId,
			idResource: row.id,
		};
		const url = `/project/${this.codeProject}/template/${this.idTemplate}/web-services`;
		const { data: response } = await this.$http.get(url, { headers, params });
		if (this.resource.permission) {
			const exitServices = Object.keys(this.resource.permission);
			this.webServices = response.data.map((d) => {
				const newService = { ...d };
				const index = exitServices.findIndex(s => Number(s) === d.id);
				newService.isSelected = index > -1;
				newService.authorize = index > -1 ? this.resource.permission[d.id] : d.authorize;
				return newService;
			});
		} else {
			this.webServices = response.data;
		}
		this.action = { ...this.actionListService };
		this.action.title = row.name;
	} catch (error) {
		this.showError();
	}
}

function saveWebServices(rows) {
	const selectRows = rows.filter(r => r.isSelected);
	let permission = {};
	if (selectRows.length) {
		permission = selectRows.reduce((acum, item) => {
			const newAcum = { ...acum };
			newAcum[item.id] = item.authorize;
			return newAcum;
		}, {});
	}
	this.defaultAction();
	this.resource.permission = { ...permission };
	this.resource.flagPermission = true;
	this.resource.flagStatus = true;
	this.action.code = 'resource';
	this.saveData(this.resource, this.resource.position);
}

function selectAllServices(value) {
	this.checkAllServices = value;
	this.webServices = this.webServices.map(setNewProperty('isSelected', value));
}

function rowIsSelectedServices(row) {
	this.webServices = this.selectIsRow(this.webServices, 'isSelected', row);
	this.checkAllServices = this.webServices.every(s => s.isSelected);
}
function data() {
	return {
		action: {
			code: null,
			open: false,
			title: '',
		},
		actionNewRol: {
			code: 'rol',
			open: true,
			title: 'Nuevo rol',
		},
		actions: [],
		actionBarActions: {
			code: 'bar-actions',
			open: true,
			title: 'Asignar barra de acciones',
		},
		actionModule: {
			code: 'module',
			open: true,
			title: 'Configuración del módulo',
		},
		actionListService: {
			code: 'list-service',
			open: true,
			title: 'Nombre del recurso',
		},
		actionResource: {
			code: 'resource',
			open: true,
			title: 'Configuración de Recurso',
		},
		app: {},
		apps: [],
		codeProject: null,
		columns: [
			{ id: 1, name: 'Nombre del Recurso', checked: true, visible: true },
			{ id: 2, name: 'URL', checked: true, visible: true },
			{ id: 3, name: 'Recurso Padre', checked: true, visible: true },
			{ id: 7, name: 'Nombres', checked: true, visible: true },
			{ id: 4, name: 'Posición', checked: true, visible: true },
			{ id: 6, name: 'Ícono', checked: true, visible: true },
			{ id: 5, name: 'Acciones', checked: true, visible: true },
		],
		checkAllResources: false,
		configurationRequired: false,
		distributionQuestion: false,
		finalizing: false,
		hasSavedConfigurationChanges: false,
		showMenuPreview: false,
		idTemplate: null,
		templateCode: null,
		languages: [],
		model: {},
		module: {},
		modules: [],
		params: {
			codeApp: null,
			codeRole: null,
			idModule: null,
			idResource: null,
			page: null,
			perPage: null,
		},
		pendingResourceConfiguration: null,
		resource: {
			pathIcon: null,
		},
		resourceFilter: 'all',
		resourceSearch: '',
		resourceSelectionBaseline: {},
		resourceSelectionUndo: {},
		resources: [],
		roles: [],
		rolSelected: {},
		temporalSelected: [],
		dataSelected: [],
		webServices: [],
		dataSelectdServices: [],
		changeResource: {},
		checkAllServices: false,
	};
}

export default {
	name: 'page-resources-assignment',
	components: {
		actionSidebar,
		appButton,
		appTooltipButton,
		barActions,
		newRolComponent,
		resourceConfiguration,
		resourceWebService,
	},
	computed: {
		activeResourcesCount,
		appsExist,
		chooseAction,
		chooseResource,
		configuredModulesPreview,
		getAppName,
		getModuleName,
		hasSelectedApplication,
		hasSelectedModule,
		hasSelectedRole,
		canFinishConfiguration,
		enabledAppsCount,
		enabledModulesCount,
		inactiveResourcesCount,
		modelEmpty,
		modulesExist,
		nextModulePosition,
		roleCode,
		noResourcesForFilter,
		pendingChangesCount,
		pendingResourcesCount,
		resourceFilters,
		resourcesComputed,
		resourcesExist,
		sortedApps,
		sortedModules,
		suggestedPosition,
	},
	created,
	data,
	methods: {
		appUseIcon,
		canMoveResource,
		cancelAction,
		cancelResourceActivation,
		clearResourceFilters,
		changeApplication,
		changeModule,
		clearData,
		clearIdModuleAnResources,
		clearTemporalSelected,
		closeAssignmentSidebar,
		closeConfiguration,
		closeRolModal,
		checkForResourceIds,
		checkForRoleIds,
		changeResourcePosition,
		configured,
		createTemplateLanguages,
		defaultAction,
		deleteApplicationConfiguration,
		deleteConfiguration,
		deleteModuleOnly,
		deleteModuleConfiguration,
		evaluateTemplateLanguages,
		getActionsSelected,
		inverseValue,
		loadLanguages,
		loadResources,
		loadServices,
		getData,
		getLanguages,
		isConfigured,
		itemLanguages,
		matchesResourceFilter,
		matchesResourceSearch,
		moveResource,
		syncCheckAllResources,
		resourceLanguagesStatus,
		getResourceName,
		hideBrokenImage,
		newRol,
		openActionsBarModal,
		openModuleAndResourceConfiguration,
		openMenuPreview,
		assignmentIcon,
		assignmentSubtitle,
		moduleSubtitle,
		moduleUseIcon,
		moduleIcon,
		moduleDisplayName,
		modulePositionLabel,
		moduleResourcesForPreview,
		normalizeResourcePositions,
		prepareCurrentResourcesForSave,
		queueResourcePosition,
		queueConfigurationItem,
		resourceSelectAction,
		resourceIcon,
		resourceLanguages,
		resourcePosition,
		resourceDisplayName,
		resourcePositionLabel,
		resourceSelectionKey,
		rowIsSelected,
		saveBarActionData,
		saveAssignmentChanges,
		saveData,
		saveInfo,
		selectAll,
		selectAllResources,
		selectModuleFromPreview,
		showModulePicker,
		syncCurrentAppConfiguration,
		isCurrentModule,
		setDefaultValues,
		setImageResource,
		setNewRole,
		updateCurrentModuleOrResource,
		updateIsActiveProperty,
		updateResources,
		updateRoleAndResources,
		updateRoleIdsInfo,
		saveWebServices,
		selectAllServices,
		rowIsSelectedServices,
		finishConfiguration,
		stayOnAssignment,
		goToDistribution,
	},
};
</script>
<style lang="scss" scoped>
.resources-assignment-page {
	height: calc(100vh - 80px);
	overflow-x: hidden;
	overflow-y: auto;
	padding-bottom: 58px;
	transition: padding-left .22s ease;
	scrollbar-color: #9db8d1 #edf2f7;
	scrollbar-width: thin;

	&::-webkit-scrollbar { width: 9px; }
	&::-webkit-scrollbar-track { background: #edf2f7; }
	&::-webkit-scrollbar-thumb {
		background: #9db8d1;
		border: 2px solid #edf2f7;
		border-radius: 999px;
	}
	&::-webkit-scrollbar-thumb:hover { background: #6f96b8; }
	&.has-menu-builder { padding-left: 230px; }
	&.menu-builder-empty { padding-left: 202px; }
	&.menu-builder-empty .menu-preview-panel { width: 188px; }
}

.resources-assignment-page > .modal-filter-footer {
	bottom: 34px;
	z-index: 3;
}

.assignment-finish-button {
	background: #1A4338 !important;
	box-shadow: 0 5px 12px rgba(26, 67, 56, .20) !important;
	min-width: 190px !important;
}

.distribution-question-card {
	background: #fff;
	border: 1px solid rgba(26, 67, 56, .16);
	border-radius: 16px !important;
	box-shadow: 0 22px 55px rgba(25, 53, 79, .22) !important;
	overflow: hidden;
	position: relative;
}

.distribution-question-accent {
	background: linear-gradient(90deg, #1A4338, #2E6B59);
	height: 4px;
	left: 0;
	position: absolute;
	right: 0;
	top: 0;
}

.distribution-question-title {
	align-items: center;
	background: linear-gradient(135deg, #F4F8F6, #EEF5F2);
	border-bottom: 1px solid #DFEBE7;
	display: flex;
	gap: 14px;
	padding: 23px 24px 19px !important;
}

.distribution-question-heading {
	flex: 1;
	min-width: 0;

	strong,
	small {
		display: block;
	}

	strong {
		color: #243f57;
		font-size: 18px;
		line-height: 1.25;
		margin-top: 3px;
	}

	small {
		color: #1A4338;
		font-size: 9px;
		font-weight: 700;
		letter-spacing: .12em;
	}
}

.distribution-question-close {
	align-items: center;
	background: transparent;
	border: 0;
	border-radius: 8px;
	cursor: pointer;
	display: flex;
	height: 32px;
	justify-content: center;
	width: 32px;

	.icon { color: #7890a3 !important; font-size: 20px; }
	&:hover { background: rgba(46, 107, 89, .09); }
}

.distribution-question-icon {
	align-items: center;
	background: linear-gradient(135deg, #1A4338, #2E6B59);
	border-radius: 12px;
	box-shadow: 0 7px 16px rgba(46, 107, 89, .24);
	display: inline-flex;
	height: 46px;
	justify-content: center;
	width: 46px;
}

.distribution-question-icon .icon {
	color: #fff !important;
	font-size: 23px;
}

.distribution-question-content {
	padding: 22px 24px 8px !important;

	h3 { color: #29465e; font-size: 16px; margin: 0 0 7px; }
	p { color: #6f8395; font-size: 12px; line-height: 1.55; margin: 0; }
}

.distribution-question-note {
	align-items: flex-start;
	background: #F4F8F6;
	border: 1px solid #DFEBE7;
	border-radius: 10px;
	color: #587086;
	display: flex;
	font-size: 11px;
	gap: 9px;
	line-height: 1.45;
	margin-top: 15px;
	padding: 10px 12px;

	.icon { color: #1A4338 !important; font-size: 18px; }
}

.distribution-question-actions {
	display: flex;
	gap: 10px;
	justify-content: flex-end;
	padding: 16px 24px 22px !important;

	.btn { border-radius: 9px; height: 38px !important; margin: 0 !important; text-transform: none; }
}

.distribution-secondary-action {
	border: 1px solid #d5e1ea !important;
	color: #526b80 !important;
}

.distribution-primary-action {
	box-shadow: 0 7px 16px rgba(26, 67, 56, .22) !important;
	min-width: 150px;
}

.items-arragnment {
	display: flex;
	flex-wrap: wrap;
}

.responsive-grid {
	display: grid;
	grid-gap: 9px;
	grid-template-columns: repeat(auto-fill, minmax(248px, 1fr));
}

.module-section {
	background-color: #fff;
}

.assignment-toolbar {
	align-items: center;
	background: linear-gradient(90deg, #F4F8F6 0%, #F4F8F6 100%);
	border-bottom: 1px solid #e2eaf2;
	display: flex;
	gap: 10px;
	margin: 0 !important;
	min-height: 56px;
	padding-bottom: 7px;
	padding-top: 7px;
}

.role-picker {
	flex: 0 1 250px;
	max-width: 250px;
}

.role-create-action {
	align-items: center;
	display: flex;
	flex: 0 0 auto;
	gap: 8px;
	max-width: max-content;

	.btn { margin: 0 !important; }
}

.create-role-button {
	border-radius: 8px;
	box-shadow: 0 3px 9px rgba(26, 67, 56, .20) !important;
	height: 36px;
	margin: 0;
	padding: 0 14px;
	text-transform: none;
	.icon { font-size: 19px; margin-left: 5px; }
}

.assignment-empty-state {
	align-items: center;
	color: #74879a;
	display: flex;
	flex-direction: column;
	justify-content: center;
	margin: auto;
	max-width: 620px;
	min-height: calc(100vh - 230px);
	padding: 28px 20px 80px;
	text-align: center;

	> small { color: #1A4338; font-size: 9px; font-weight: 700; letter-spacing: .14em; margin-top: 13px; }
	h2 { color: #263f57; font-size: 23px; margin: 5px 0 7px; }
	p { font-size: 12px; line-height: 1.55; margin: 0; max-width: 430px; }
}

.assignment-empty-icon {
	align-items: center;
	background: linear-gradient(135deg, #EAF3EF, #DCEAE4);
	border: 1px solid #C4DBD3;
	border-radius: 16px;
	box-shadow: 0 8px 24px rgba(26, 67, 56, .12);
	display: inline-flex;
	height: 62px;
	justify-content: center;
	width: 62px;
	.icon { color: #1A4338; font-size: 30px; }
}

.assignment-empty-steps {
	align-items: center;
	background: rgba(255, 255, 255, .72);
	border: 1px solid #e0e9f1;
	border-radius: 12px;
	display: flex;
	gap: 9px;
	margin-top: 20px;
	padding: 8px 12px;

	span { align-items: center; color: #536b80; display: inline-flex; font-size: 10px; font-weight: 600; gap: 5px; white-space: nowrap; }
	i { align-items: center; background: #EAF3EF; border-radius: 50%; color: #1A4338; display: inline-flex; font-size: 9px; font-style: normal; height: 20px; justify-content: center; width: 20px; }
	.icon { color: #a6b4c0; font-size: 15px; }
}

.assignment-panel {
	background: white;
	border: 1px solid #dfe7ef;
	border-radius: 12px;
	box-shadow: 0 3px 12px rgba(31, 56, 82, .06);
	margin-top: 9px;
	padding: 12px 14px 14px;
}

.assignment-progress {
	align-items: center;
	background: white;
	border-bottom: 1px solid #e5ebf1;
	display: flex;
	gap: 7px;
	min-height: 42px;

	> .icon { color: #b1bdc8; font-size: 17px; }
}

.progress-step {
	align-items: center;
	color: #93a1ae;
	display: inline-flex;
	font-size: 11px;
	font-weight: 600;
	gap: 6px;

	i {
		align-items: center;
		background: #edf1f5;
		border-radius: 50%;
		display: inline-flex;
		font-size: 9px;
		font-style: normal;
		height: 21px;
		justify-content: center;
		width: 21px;
	}

	&.is-active { color: #1A4338; i { background: #1A4338; color: white; } }
	&.is-complete { color: #23834a; i { background: #dff3e7; color: #23834a; } }
}

.assignment-section-header {
	align-items: center;
	display: flex;
	justify-content: space-between;
	margin-bottom: 10px;

	> div:first-child {
		align-items: center;
		display: flex;
		gap: 7px;
	}

	h3 {
		color: #2b435b;
		font-family: map-get($fonts, open-semibold);
		font-size: 14px;
		margin: 0;
	}

	.icon { font-size: 20px; }
}

.assignment-summary {
	align-items: center;
	color: #8191a1;
	display: flex;
	font-size: 10px;
	gap: 7px;

	span { background: #f0f3f6; border-radius: 999px; padding: 5px 9px; }
	.enabled-count { background: #e3f6eb; color: #23834a; font-weight: 700; }
}

.selection-path {
	align-items: center;
	background: #F4F8F6;
	border: 1px solid #DCE9E4;
	border-radius: 10px;
	display: flex;
	gap: 10px;
	margin-top: 8px;
	min-height: 48px;
	padding: 5px 9px;
}

.selection-path-item {
	align-items: center;
	display: flex;
	flex: 1 1 0;
	min-width: 0;

	.selection-path-copy {
		display: flex;
		flex-direction: column;
		gap: 2px;
		margin-left: 10px;

		small { color: #8495a5; font-size: 8px; text-transform: uppercase; }
		strong { color: #29445e; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	}

	.change-selection-button {
		align-items: center;
		background: white;
		border: 1px solid #cbddeb;
		border-radius: 7px;
		color: #1A4338;
		cursor: pointer;
		display: flex;
		font-size: 10px;
		font-weight: 600;
		height: 28px;
		margin-left: auto;
		min-width: 28px;
		padding: 0;
		width: 28px;
		.icon { color: #1A4338; font-size: 15px; }
		&:hover { background: #F4F8F6; }
	}
}

.path-actions {
	align-items: center;
	display: flex;
	margin-left: auto;
	white-space: nowrap;
}

.path-status {
	background: #e3f6eb;
	border-radius: 999px;
	color: #23834a;
	font-size: 9px;
	font-weight: 700;
	margin-right: 4px;
	padding: 4px 7px;
}

.path-icon {
	align-items: center;
	border-radius: 7px;
	display: inline-flex;
	flex: 0 0 30px;
	height: 30px;
	justify-content: center;
	width: 30px;
	.icon { font-size: 16px; }
}

.path-divider { color: #a4b3c1; font-size: 18px; }

.app-selection { background: linear-gradient(135deg, #2E6B59, #1A4338); }
.module-selection { background: linear-gradient(135deg, #35aa6e, #23834a); }

.fade-down-enter-active {
  transition: all .4s ease;
}

.fade-down-leave-active {
  transition: all .8s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.fade-down-enter, .fade-down-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.resource-table-card {
	background: white;
	border: 1px solid #dfe6ee;
	border-radius: 8px;
	box-shadow: 0 4px 14px rgba(31, 56, 82, .08);
	overflow: hidden;
}

.resource-table-toolbar {
	align-items: center;
	background: #fff;
	border-bottom: 1px solid #eef2f7;
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	justify-content: space-between;
	padding: 11px 16px;
}

.resource-search {
	align-items: center;
	background: #f4f7fa;
	border: 1px solid #e2e9f0;
	border-radius: 9px;
	display: flex;
	flex: 1 1 240px;
	gap: 7px;
	height: 36px;
	max-width: 380px;
	padding: 0 10px;
	transition: border-color .15s ease, box-shadow .15s ease;

	> .icon { color: #a9b6c2; font-size: 18px; }

	input {
		background: transparent;
		border: 0;
		color: #2b435b;
		flex: 1 1 auto;
		font-size: 12px;
		min-width: 0;
		outline: none;

		&::placeholder { color: #a9b6c2; }
	}

	button {
		align-items: center;
		background: #e2e9f0;
		border: 0;
		border-radius: 50%;
		cursor: pointer;
		display: flex;
		height: 18px;
		justify-content: center;
		outline: none;
		width: 18px;

		.icon { color: #6b7c8d; font-size: 13px; }
		&:hover { background: #d3dde7; }
	}

	&:focus-within,
	&.is-filled {
		background: #fff;
		border-color: #1a4338;
		box-shadow: 0 0 0 3px rgba(26, 67, 56, .1);
	}
}

.resource-filter-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.resource-filter-chip {
	align-items: center;
	background: #f4f7fa;
	border: 1px solid #e2e9f0;
	border-radius: 20px;
	color: #6b7c8d;
	cursor: pointer;
	display: inline-flex;
	font-size: 11px;
	font-weight: 700;
	gap: 6px;
	height: 32px;
	outline: none;
	padding: 0 12px;
	transition: background .15s ease, border-color .15s ease, color .15s ease;

	strong {
		background: #e5ebf1;
		border-radius: 9px;
		color: #53697d;
		font-size: 10px;
		min-width: 20px;
		padding: 2px 5px;
		text-align: center;
	}

	&:hover { border-color: #c9d6e2; }

	&.is-active {
		background: #1a4338;
		border-color: #1a4338;
		color: #fff;

		strong { background: rgba(255, 255, 255, .18); color: #fff; }
	}

	&--pending {
		strong { background: #fdf1dc; color: #a6711a; }

		&.is-active {
			background: #a6711a;
			border-color: #a6711a;
		}
	}
}

.resource-filter-empty {
	align-items: center;
	background: #f7f9fc;
	border-bottom: 1px solid #e4eaf0;
	display: flex;
	gap: 11px;
	padding: 13px 16px;

	> .icon { color: #a9b6c2; font-size: 22px; }

	strong { color: #2b435b; display: block; font-size: 13px; }
	small { color: #8495a5; font-size: 11px; }

	div { flex: 1 1 auto; }

	button {
		background: #fff;
		border: 1px solid #d4dee7;
		border-radius: 8px;
		color: #176fc1;
		cursor: pointer;
		font-size: 11px;
		font-weight: 700;
		height: 32px;
		outline: none;
		padding: 0 12px;

		&:hover { background: #f1f5f9; }
	}
}

.summary-chip-pending {
	align-items: center;
	background: #fdf1dc;
	color: #a6711a;
	display: inline-flex;
	gap: 5px;

	.icon { color: #e19a18; font-size: 15px; }
}

.position-control {
	align-items: center;
	display: flex;
	gap: 4px;
	justify-content: center;
}

.position-steppers {
	display: flex;
	flex-direction: column;
	gap: 2px;

	button {
		align-items: center;
		background: #f1f5f9;
		border: 1px solid #e2e9f0;
		border-radius: 5px;
		cursor: pointer;
		display: flex;
		height: 15px;
		justify-content: center;
		outline: none;
		padding: 0;
		width: 22px;

		.icon { color: #53697d; font-size: 14px; }

		&:hover:not(:disabled) { background: #e2e9f0; }

		&:disabled {
			cursor: default;
			opacity: .4;
			.icon { color: #a9b6c2; }
		}
	}
}

.resource-table-guide {
	align-items: center;
	background: #f7f9fc;
	border-bottom: 1px solid #e4eaf0;
	display: flex;
	justify-content: space-between;
	min-height: 52px;
	padding: 8px 16px;
}

.resource-table-guide-text,
.resource-table-summary {
	align-items: center;
	display: flex;
	gap: 8px;
}

.resource-table-guide-text {
	color: #526271;
}

.summary-chip {
	background: #e8edf2;
	border-radius: 12px;
	color: #536473;
	font-size: 12px;
	font-weight: 600;
	padding: 4px 10px;
}

.summary-chip-active {
	background: #dff3e6;
	color: #23834a;
}

.menu-preview-trigger {
	align-items: center;
	background: #F4F8F6;
	border: 1px solid #C4DBD3;
	border-radius: 8px;
	color: #1A4338;
	cursor: pointer;
	display: inline-flex;
	font-size: 11px;
	font-weight: 700;
	gap: 6px;
	height: 32px;
	padding: 0 8px 0 10px;
	white-space: nowrap;

	.icon { color: #1A4338; font-size: 18px; }
	strong {
		align-items: center;
		background: #1A4338;
		border-radius: 999px;
		color: white;
		display: inline-flex;
		font-size: 10px;
		height: 20px;
		justify-content: center;
		min-width: 20px;
		padding: 0 5px;
	}
	&:hover { background: #E4F0EB; border-color: #70A997; }
}

.menu-preview-backdrop {
	background: rgba(20, 39, 58, .32);
	border: 0;
	bottom: 0;
	cursor: default;
	left: 0;
	padding: 0;
	position: fixed;
	right: 0;
	top: 56px;
	z-index: 8;
	display: none;
}

.menu-preview-panel {
	background: #f8fafc;
	border-right: 1px solid #dbe5ee;
	bottom: 0;
	box-shadow: 12px 0 30px rgba(27, 51, 75, .18);
	display: flex;
	flex-direction: column;
	left: 0;
	position: fixed;
	top: 56px;
	width: 230px;
	z-index: 9;
}

.menu-preview-header {
	align-items: center;
	background: white;
	border-bottom: 1px solid #e1e9f0;
	display: flex;
	justify-content: space-between;
	min-height: 74px;
	padding: 12px 16px;

	> div { align-items: center; display: flex; gap: 10px; }
	small { color: #6f8294; display: block; font-size: 9px; font-weight: 700; letter-spacing: .1em; }
	h3 { color: #20394f; font-size: 17px; line-height: 1.25; margin: 3px 0 0; }
	> button {
		align-items: center;
		background: #f0f4f7;
		border: 0;
		border-radius: 7px;
		cursor: pointer;
		display: none;
		height: 30px;
		justify-content: center;
		width: 30px;
		.icon { color: #53697d; font-size: 18px; }
	}
}

.menu-preview-mobile-trigger {
	align-items: center;
	background: #F4F8F6;
	border: 1px solid #C8DDD5;
	border-radius: 7px;
	cursor: pointer;
	display: none;
	height: 28px;
	justify-content: center;
	margin-left: 4px;
	width: 28px;
	.icon { color: #1A4338; font-size: 16px; }
}

.menu-preview-icon {
	align-items: center;
	background: linear-gradient(135deg, #2E6B59, #1A4338);
	border-radius: 9px;
	display: inline-flex;
	height: 42px;
	justify-content: center;
	width: 42px;
	.icon { font-size: 22px; }
}

.menu-preview-context {
	align-items: center;
	background: #F4F8F6;
	border-bottom: 1px solid #D7E6E0;
	display: flex;
	gap: 8px;
	padding: 9px 14px;

	> .icon { color: #1A4338; font-size: 18px; }
	> div { display: flex; flex: 1; flex-direction: column; min-width: 0; }
	small { color: #74889a; font-size: 8px; text-transform: uppercase; }
	strong { color: #29445e; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	> span { color: #53718c; font-size: 9px; white-space: nowrap; }
}

.menu-preview-list {
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 6px;
	min-height: 0;
	overflow-y: auto;
	padding: 10px;
}

.menu-preview-group {
	background: white;
	border: 1px solid #dfe7ef;
	border-radius: 9px;
	flex: 0 0 auto;
	overflow: hidden;
	transition: border-color .15s ease, box-shadow .15s ease;

	&:hover { border-color: #70A997; box-shadow: 0 3px 9px rgba(26, 67, 56, .10); }
	&.is-current {
		background: #edf8f2;
		border-color: #74c99a;
		.menu-preview-position { background: #27945a; }
		.menu-preview-item > .icon { color: #27945a; }
	}
}

.menu-preview-item {
	align-items: center;
	background: transparent;
	border: 0;
	cursor: pointer;
	display: flex;
	gap: 10px;
	min-height: 52px;
	padding: 6px 9px;
	text-align: left;
	transition: background .15s ease;
	width: 100%;

	> .icon { color: #91a1af; font-size: 18px; }
	&:hover { background: rgba(26, 67, 56, .05); }
}

.menu-preview-module-row {
	align-items: center;
	display: flex;

	.menu-preview-item { flex: 1; min-width: 0; }
}

.menu-preview-delete-module {
	align-items: center;
	background: rgba(232, 73, 73, .12);
	border: 0;
	border-radius: 7px;
	cursor: pointer;
	display: inline-flex;
	flex: 0 0 30px;
	height: 30px;
	justify-content: center;
	margin-right: 7px;
	transition: background .15s ease;

	.icon { color: #ffb3b3 !important; font-size: 17px; }
	&:hover { background: rgba(232, 73, 73, .3); }
}

.menu-preview-position {
	align-items: center;
	background: #1A4338;
	border-radius: 8px;
	color: white;
	display: inline-flex;
	font-size: 12px;
	font-weight: 700;
	height: 34px;
	justify-content: center;
	min-width: 34px;
}

.menu-entry-icon {
	align-items: center;
	background: #edf3f8;
	border: 1px solid #d6e2ec;
	border-radius: 7px;
	display: inline-flex;
	flex: 0 0 auto;
	justify-content: center;
	overflow: hidden;
	position: relative;

	.icon { color: #6e879b; }
	img {
		background: inherit;
		height: 78%;
		object-fit: contain;
		position: absolute;
		width: 78%;
		&.is-hidden { display: none; }
	}
}

.module-menu-icon {
	height: 32px;
	width: 32px;
	.icon { font-size: 17px; }
}

.resource-menu-icon {
	border-radius: 5px;
	height: 23px;
	width: 23px;
	.icon { font-size: 14px !important; }
}

.menu-preview-name {
	display: flex;
	flex: 1;
	flex-direction: column;
	min-width: 0;
	strong { color: #2d465d; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	small { color: #8393a2; font-size: 9px; margin-top: 2px; }
}

.menu-preview-submenu {
	border-top: 1px solid #e8eef3;
	display: flex;
	flex-direction: column;
	margin: 0 9px 7px 52px;
	padding-top: 4px;

	button {
		align-items: center;
		background: transparent;
		border: 0;
		border-radius: 6px;
		color: #526b80;
		cursor: pointer;
		display: flex;
		gap: 5px;
		min-height: 30px;
		padding: 3px 5px;
		text-align: left;
		width: 100%;
		&:hover { background: #EAF3EF; color: #1A4338; }
		> span { color: #90a0ae; font-size: 9px; min-width: 13px; }
		> .icon { color: #668F82; font-size: 14px; }
		strong { font-size: 10px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	}
}

.menu-preview-no-submenu {
	border-top: 1px solid #e8eef3;
	color: #9aa7b3;
	font-size: 9px;
	font-style: italic;
	margin: 0 9px 7px 52px;
	padding: 7px 5px 2px;
}

.menu-preview-empty {
	align-items: center;
	color: #8191a1;
	display: flex;
	flex: 1;
	flex-direction: column;
	justify-content: center;
	padding: 24px;
	text-align: center;
	.icon { color: #82A69A; font-size: 42px; }
	strong { color: #415c74; font-size: 13px; margin: 8px 0 4px; }
	span { font-size: 11px; line-height: 1.45; }
}

.add-module-button {
	align-items: center;
	background: #F4F8F6;
	border: 1px dashed #70A997;
	border-radius: 10px;
	color: #1A4338;
	cursor: pointer;
	display: flex;
	flex: 0 0 auto;
	gap: 9px;
	margin: 8px 10px;
	min-height: 48px;
	padding: 6px 9px;
	text-align: left;
	transition: background .15s ease, border-color .15s ease, transform .15s ease;
	width: calc(100% - 20px);

	> span {
		align-items: center;
		background: #1A4338;
		border-radius: 8px;
		display: inline-flex;
		height: 32px;
		justify-content: center;
		width: 32px;
		.icon { color: white; font-size: 19px; }
	}
	> div { display: flex; flex: 1; flex-direction: column; min-width: 0; }
	strong { color: #1A4338; font-size: 11px; }
	small { color: #71889c; font-size: 9px; margin-top: 1px; }
	> .icon { color: #5D8F7F; font-size: 18px; }
	&:hover { background: #EAF3EF; border-color: #1A4338; transform: translateY(-1px); }
}

.menu-preview-panel footer {
	align-items: center;
	background: white;
	border-top: 1px solid #e1e9f0;
	color: #758697;
	display: flex;
	font-size: 9px;
	gap: 6px;
	min-height: 38px;
	padding: 7px 12px;
	.icon { color: #1A4338; font-size: 16px; }
}

/* Constructor lateral con apariencia de menú final. */
.menu-preview-panel {
	background:
		radial-gradient(circle at 15% 5%, rgba(70, 135, 114, .26), transparent 28%),
		linear-gradient(180deg, #1A4B3E 0%, #163D33 48%, #0D2A23 100%);
	border-right-color: rgba(255, 255, 255, .15);
	box-shadow: 12px 0 32px rgba(8, 36, 29, .28);
}

.menu-preview-header {
	background: rgba(8, 39, 31, .34);
	border-bottom-color: rgba(255, 255, 255, .13);
	backdrop-filter: blur(8px);

	small { color: #B5D7CB; }
	h3 { color: #fff; }
	> button {
		background: rgba(255, 255, 255, .12);
		.icon { color: #fff; }
	}
}

.menu-preview-icon {
	background: rgba(255, 255, 255, .16);
	border: 1px solid rgba(255, 255, 255, .25);
	box-shadow: inset 0 1px 0 rgba(255, 255, 255, .14);
}

.menu-preview-context {
	background: rgba(8, 36, 29, .25);
	border-bottom-color: rgba(255, 255, 255, .11);
	> .icon { color: #fff; }
	small { color: #9FC2B6; }
	strong { color: #fff; }
	> span { color: #C2DBD2; }
}

.menu-preview-list {
	scrollbar-color: rgba(255, 255, 255, .34) transparent;
	scrollbar-width: thin;
	&::-webkit-scrollbar { width: 6px; }
	&::-webkit-scrollbar-track { background: transparent; }
	&::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, .3); border-radius: 999px; }
}

.menu-preview-group {
	background: rgba(255, 255, 255, .08);
	border-color: rgba(255, 255, 255, .15);
	box-shadow: inset 0 1px 0 rgba(255, 255, 255, .05);
	&:hover {
		background: rgba(255, 255, 255, .13);
		border-color: rgba(139, 194, 175, .65);
		box-shadow: 0 7px 18px rgba(5, 28, 22, .20);
	}
	&.is-current {
		background: linear-gradient(135deg, rgba(46, 107, 89, .96), rgba(26, 67, 56, .96));
		border-color: rgba(190, 222, 211, .78);
		box-shadow: 0 8px 22px rgba(5, 31, 25, .28), inset 0 1px 0 rgba(255, 255, 255, .2);
		.menu-preview-position { background: #fff; color: #1A4338; }
		.menu-preview-item > .icon { color: #fff; }
	}
}

.menu-preview-item {
	&:hover { background: rgba(255, 255, 255, .06); }
	> .icon { color: #CBE2DA; }
}

.menu-preview-position {
	background: rgba(255, 255, 255, .16);
	border: 1px solid rgba(255, 255, 255, .22);
	color: #fff;
}

.menu-preview-name {
	strong { color: #fff; }
	small { color: #C2DBD2; }
}

.menu-entry-icon {
	background: rgba(255, 255, 255, .12);
	border-color: rgba(255, 255, 255, .2);
	.icon { color: #fff; }
	img { background: transparent; filter: grayscale(1) brightness(0) invert(1); }
}

.menu-preview-submenu {
	border-top-color: rgba(255, 255, 255, .14);
	button {
		color: #F4F8F6;
		&:hover { background: rgba(255, 255, 255, .12); color: #fff; }
		> span { color: #B2D0C6; }
		strong { color: #fff; }
	}
}

.menu-preview-no-submenu {
	border-top-color: rgba(255, 255, 255, .13);
	color: #B2D0C6;
}

.menu-preview-empty {
	color: #BDD7CE;
	.icon { color: #B5D7CB; }
	strong { color: #fff; }
}

.add-module-button {
	background: rgba(255, 255, 255, .09);
	border-color: rgba(139, 194, 175, .70);
	> span { background: #fff; .icon { color: #245B4C; } }
	strong { color: #fff; }
	small { color: #C2DBD2; }
	> .icon { color: #D8E9E3; }
	&:hover { background: rgba(255, 255, 255, .16); border-color: #fff; }
}

.menu-preview-panel footer {
	background: rgba(7, 31, 25, .38);
	border-top-color: rgba(255, 255, 255, .12);
	color: #BDD7CE;
	.icon { color: #fff; }
}

/* Variante premium: menos cajas y mayor claridad visual. */
.menu-preview-panel {
	background: linear-gradient(180deg, #1A4338 0%, #153A31 52%, #102F28 100%);
	box-shadow: 10px 0 28px rgba(7, 31, 25, .22);
}

.menu-preview-header {
	background: transparent;
	min-height: 68px;
	padding: 10px 16px;
	small { color: rgba(205, 226, 219, .70); font-size: 8px; }
	h3 { font-size: 16px; font-weight: 600; }
}

.menu-preview-icon {
	background: rgba(255, 255, 255, .1);
	border-color: rgba(255, 255, 255, .16);
	border-radius: 10px;
	height: 38px;
	width: 38px;
	.icon { color: #fff !important; font-size: 20px; }
}

.menu-preview-context {
	background: rgba(255, 255, 255, .035);
	min-height: 48px;
	padding: 7px 16px;
	> .icon { color: #CBE2DA !important; font-size: 17px; }
	small { color: rgba(211, 231, 224, .62); }
	strong { font-size: 11px; font-weight: 600; }
	> span { color: rgba(218, 235, 229, .72); }
}

.menu-preview-list {
	gap: 4px;
	padding: 8px;
}

.menu-preview-group {
	background: transparent;
	border-color: transparent;
	border-radius: 9px;
	box-shadow: none;
	&:hover {
		background: rgba(255, 255, 255, .065);
		border-color: transparent;
		box-shadow: none;
	}
	&.is-current {
		background: rgba(46, 107, 89, .48);
		border-color: rgba(139, 194, 175, .48);
		box-shadow: inset 3px 0 0 #8BC2AF;
		.menu-preview-position {
			background: #fff;
			border-color: #fff;
			color: #245B4C;
		}
		.menu-preview-item > .icon { color: #fff !important; }
	}
}

.menu-preview-item {
	gap: 8px;
	min-height: 46px;
	padding: 5px 9px;
	> .icon { color: rgba(226, 240, 235, .75) !important; font-size: 17px; }
}

.menu-preview-position {
	background: rgba(255, 255, 255, .1);
	border-color: transparent;
	border-radius: 7px;
	font-size: 10px;
	height: 27px;
	min-width: 27px;
}

.module-menu-icon {
	background: transparent;
	border: 0;
	height: 28px;
	width: 28px;
	.icon { color: #fff !important; font-size: 18px; }
	img { height: 88%; width: 88%; }
}

.menu-preview-name {
	strong { font-size: 11px; font-weight: 600; }
	small { color: rgba(211, 231, 224, .62); font-size: 8px; }
}

.menu-preview-submenu {
	border-left: 1px solid rgba(159, 194, 182, .25);
	border-top: 0;
	gap: 1px;
	margin: 0 8px 6px 50px;
	padding: 1px 0 2px 9px;
	button {
		min-height: 27px;
		padding: 2px 4px;
		> span:first-child { color: rgba(194, 219, 210, .62); min-width: 11px; }
		strong { font-size: 9px; font-weight: 500; }
	}
}

.resource-menu-icon {
	background: transparent !important;
	border: 0 !important;
	border-radius: 0;
	box-shadow: none;
	height: 20px;
	overflow: visible;
	width: 20px;
	.icon { color: #fff !important; font-size: 13px !important; }
	img {
		background: transparent !important;
		border: 0;
		border-radius: 0;
		box-shadow: none;
		filter: grayscale(1) brightness(0) invert(1);
		height: 16px;
		width: 16px;
	}
}

.add-module-button {
	background: rgba(255, 255, 255, .045);
	border-color: rgba(178, 208, 198, .42);
	border-radius: 9px;
	margin: 7px 9px;
	min-height: 44px;
	> span {
		background: rgba(255, 255, 255, .12);
		height: 29px;
		width: 29px;
		.icon { color: #fff !important; }
	}
	> .icon { color: rgba(225, 239, 234, .75) !important; }
	&:hover { background: rgba(255, 255, 255, .09); }
}

.menu-preview-panel footer {
	background: rgba(6, 27, 22, .24);
	color: rgba(208, 228, 221, .70);
	.icon { color: rgba(226, 240, 235, .78) !important; }
}

.menu-preview-slide-enter-active,
.menu-preview-slide-leave-active,
.menu-preview-fade-enter-active,
.menu-preview-fade-leave-active { transition: all .2s ease; }
.menu-preview-slide-enter,
.menu-preview-slide-leave-to { transform: translateX(-100%); }
.menu-preview-fade-enter,
.menu-preview-fade-leave-to { opacity: 0; }

.resource-languages-cell {
	align-items: center;
	display: flex;
	gap: 6px;
	justify-content: center;
}

.language-chip {
	align-items: center;
	background: #f0f2f5;
	border: 1px solid #dde3ea;
	border-radius: 11px;
	color: #8495a5;
	display: inline-flex;
	font-size: 11px;
	font-weight: 700;
	gap: 3px;
	line-height: 1;
	padding: 3px 8px 3px 6px;

	.icon { color: #b4bfca; font-size: 14px; }
}

.language-chip-filled {
	background: #dff3e6;
	border-color: #bde3cb;
	color: #23834a;

	.icon { color: #2eaa64; }
}

.resource-icon-preview {
	align-items: center;
	background-color: #dfe5eb;
	background-image:
		linear-gradient(45deg, #cbd3da 25%, transparent 25%),
		linear-gradient(-45deg, #cbd3da 25%, transparent 25%),
		linear-gradient(45deg, transparent 75%, #cbd3da 75%),
		linear-gradient(-45deg, transparent 75%, #cbd3da 75%);
	background-position: 0 0, 0 4px, 4px -4px, -4px 0;
	background-size: 8px 8px;
	border: 1px solid #bcc6cf;
	border-radius: 4px;
	display: inline-flex;
	height: 34px;
	justify-content: center;
	position: relative;
	width: 34px;

	img {
		height: 28px;
		object-fit: contain;
		position: absolute;
		width: 28px;

		&.is-hidden {
			display: none;
		}
	}
}

.resource-icon-empty {
	color: #667684;
	font-size: 16px;
	font-weight: bold;
}

.resource-position-cell {
	padding-bottom: 3px !important;
	padding-top: 3px !important;
}

@media (max-width: 1100px) {
	.resources-assignment-page.has-menu-builder { padding-left: 0; }
	.menu-preview-panel {
		transform: translateX(-105%);
		transition: transform .2s ease;
		width: min(84vw, 240px);
		&.is-open { transform: translateX(0); }
	}
	.menu-preview-backdrop { display: block; }
	.menu-preview-header > button { display: flex; }
	.menu-preview-mobile-trigger { display: flex; }
}

@media (max-width: 700px) {
	.resources-assignment-page { height: calc(100vh - 133px); padding-bottom: 66px; }
	.assignment-toolbar {
		align-items: stretch;
		flex-direction: column;
		gap: 8px;
		padding-left: 12px !important;
		padding-right: 12px !important;

		.flex { margin-right: 0 !important; width: 100%; }
	}
	.role-picker,
	.role-create-action { max-width: 100%; }
	.assignment-empty-state { min-height: calc(100vh - 310px); padding-bottom: 40px; }
	.assignment-empty-steps { flex-wrap: wrap; justify-content: center; }

	.assignment-panel {
		margin-left: 12px !important;
		margin-right: 12px !important;
	}

	.assignment-progress {
		overflow-x: auto;
		padding-left: 12px !important;
		padding-right: 12px !important;
		white-space: nowrap;
	}

	.selection-path {
		align-items: stretch;
		flex-direction: column;
		margin-left: 12px !important;
		margin-right: 12px !important;
	}
	.selection-path-item { width: 100%; }
	.path-divider { align-self: center; transform: rotate(90deg); }

	.assignment-section-header { align-items: flex-start; flex-direction: column; gap: 8px; }
	.responsive-grid { grid-template-columns: 1fr; }

	.resource-table-guide {
		align-items: flex-start;
		flex-direction: column;
	}

	.resource-table-toolbar { align-items: stretch; flex-direction: column; }
	.resource-search { max-width: none; }

	.menu-preview-backdrop,
	.menu-preview-panel { bottom: 0; top: 56px; }
	.menu-preview-panel { width: min(92vw, 340px); }
	.menu-preview-trigger span { display: none; }
}
</style>

<style lang="scss">
.resources-assignment-page .resources-table tbody > tr.row-checked > td:first-child {
	box-shadow: inset 3px 0 0 #2eaa64;
}

.resources-assignment-page .resource-position-cell {
	height: 38px !important;
	padding: 2px 6px !important;

	.v-text-field,
	.v-input,
	.input-group {
		height: 30px !important;
		margin: 0 auto !important;
		max-height: 30px !important;
		max-width: 68px;
		min-height: 30px !important;
		padding: 0 !important;
	}

	.v-input__control,
	.v-input__slot,
	.input-group__input {
		border-radius: 6px;
		height: 30px !important;
		margin: 0 !important;
		max-height: 30px !important;
		min-height: 30px !important;
		padding: 0 8px !important;
	}

	.v-text-field__slot,
	.input-group__input { align-items: center; }

	.v-text-field__slot input,
	.input-group__input input {
		font-size: 11px;
		height: 26px !important;
		line-height: 26px;
		margin: 0 !important;
		padding: 0 !important;
	}
}
</style>



// WEBPACK FOOTER //
// src/pages/template/page-resources-assignment.vue